import React, { useState, useEffect } from 'react';
import { Mail, Lock, CheckCircle, Loader, Shield, Users, Zap, Search, Telescope,LayoutTemplate, X, ChevronLeft, ChevronRight} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { userLogin, resendActivationMail, forgotAccountPassword, loggedByGoogle } from "../api/api";
import { useAuth } from '../utils/AuthContext';
import config from '../utils/config';
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";


const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [rememberMe, setRememberMe] = useState(false);
  const [activationResendStatus, setActivationResendStatus] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Forgot Password Modal States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  
  // Features and services to showcase in the slider
  const slides = [
    {
      icon: <Search size={24} className="text-white" />,
      title: "Smart Tool Finder",
      description: "Let Tuly do the heavy lifting. Describe your task, and Tuly will break it down, analyze it, and suggest the top tools for every part of your project.",
      color: "from-blue-700 to-blue-900"
    },
    {
      icon: <LayoutTemplate size={24} className="text-white" />,
      title: "Your Tool, Your Experience",
      description: "Tried something awesome? Share your insights with the community through ratings and reviews, and help others make smarter choices.",
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: <Telescope size={24} className="text-white" />,
      title: "Explore and Contribute",
      description: "Discover tools by category, revisit your searches, and even suggest new tools—as a user or developer. Tuly grows with your input.",
      color: "from-blue-600 to-blue-800"
    }
  ];

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const handleLogin = async () => {
    // Basic validation
    if (!email.trim()) {
      setErrorMessage('Please enter your email address');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    if (!password) {
      setErrorMessage('Please enter your password');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    setLoading(true);
    try {

      const results = await userLogin(email, password);
      if(results.responsecode == 200){
        login(results.token, results.userId, results.email, results.fname, results.lname);
        navigate('/home');

      } else if(results.responsecode == 201){
        setErrorMessage('Invalid email or password');
        setTimeout(() => setErrorMessage(''), 3000);

      } else if(results.responsecode == 202){
        //setErrorMessage('Your account has not been activated yet! Check your mail box please');
        setErrorMessage('activation');
        setTimeout(() => setErrorMessage(''), 50000);
      }

    } catch (error) {
      console.error('Error > ', error);
      setErrorMessage('Login failed. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (userInfo) => {
    console.log()
    const response = await loggedByGoogle(userInfo.given_name, userInfo.family_name, userInfo.email, userInfo.picture );

    if(response.responsecode == 200){
      login(response.token, response.userId, response.email, response.fname, response.lname);
      navigate('/home');
    }
  }

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) {
      setForgotMessage('Please enter your email address');
      setTimeout(() => setForgotMessage(''), 3000);
      return;
    }

    setForgotLoading(true);
    setForgotMessage('');

    try {
      const results = await forgotAccountPassword(forgotEmail);
      
      if (results.responsecode === 200) {
        setForgotSuccess(true);
        setForgotMessage('Password reset link sent successfully! Please check your inbox.');
      } else if (results.responsecode === 404) {
        setForgotMessage('No account found with this email address.');
      } else {
        setForgotMessage(results.message || 'Failed to send reset email. Please try again.');
      }
      
      setTimeout(() => {
        setForgotMessage('');
        if (results.responsecode === 200) {
          setShowForgotPassword(false);
          setForgotEmail('');
          setForgotSuccess(false);
        }
      }, 5000);
      
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotMessage('Failed to send reset email. Please try again.');
      setTimeout(() => setForgotMessage(''), 3000);
    } finally {
      setForgotLoading(false);
    }
  };

  const openForgotPasswordModal = () => {
    setShowForgotPassword(true);
    setForgotEmail('');
    setForgotMessage('');
    setForgotSuccess(false);
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotMessage('');
    setForgotSuccess(false);
  };

  // For Enter key press handling in forgot password modal
  const handleForgotKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleForgotPassword();
    }
  };

  const handleResendActivation = async () => {
    // Add your resend email logic here
    setResendLoading(true);
    setActivationResendStatus('');

    try {
      // Call your resend email API
      const results = await resendActivationMail(email);

      if (results.responsecode == 200) {
        setActivationResendStatus('Activation email sent successfully! Please check your inbox.');
        setResendLoading(false);
        setTimeout(() => setActivationResendStatus(''), 5000);
      } else {
        setActivationResendStatus(results.message || 'Failed to send activation email. Please try again.');
        setResendLoading(false);
        setTimeout(() => setActivationResendStatus(''), 5000);
      }
      
    } catch (error) {
      setErrorMessage('Failed to resend activation email. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSignUp = () => {
    navigate('/signup');
  };

  // For Enter key press handling
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // Benefits of HUBBR
  const benefits = [
    {
      icon: <Zap size={40} className="text-blue-600" />,
      title: "Fast Onboarding",
      description: "Get started in minutes with our streamlined setup process"
    },
    {
      icon: <Users size={40} className="text-blue-600" />,
      title: "Team Collaboration",
      description: "Connect with colleagues and clients in real-time"
    },
    {
      icon: <Shield size={40} className="text-blue-600" />,
      title: "Data Protection",
      description: "Your information is secured with enterprise-grade encryption"
    }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
      <div className="w-full max-w-4xl flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Top section - Header with logo and features slider */}
        <div className="w-full bg-blue-600 p-6 sm:p-8 text-white">
          <div className="flex items-center justify-between">
            {/* Logo on the left */}
            <div className="flex-shrink-0">
              <img 
                src={`${config.IMG_URL}/tuly.png`}
                alt="Tuly" 
                className="h-14 w-auto filter brightness-0 invert"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='120' height='35' viewBox='0 0 120 35'%3e%3ctext x='0' y='28' font-family='Arial' font-size='28' font-weight='bold' fill='white'%3eTuly%3c/text%3e%3c/svg%3e";
                }}
              />
            </div>
            
            {/* Features slider on the right */}
            <div className="flex-1 max-w-md ml-8 relative">
              <div className="flex items-center">
                {/* Previous button */}
                <button
                  onClick={() => setCurrentSlide(prev => prev === 0 ? slides.length - 1 : prev - 1)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 mr-3 transition-all duration-200 flex-shrink-0"
                >
                  <ChevronLeft size={20} className="text-white" />
                </button>
                
                {/* Current slide content */}
                <div className="flex-1 min-h-[80px] flex items-center">
                  <div className="flex items-center w-full">
                    <div className="bg-white bg-opacity-20 rounded-lg p-2 mr-3 flex-shrink-0">
                      {slides[currentSlide].icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-white mb-1">{slides[currentSlide].title}</h3>
                      <p className="text-blue-100 text-xs leading-relaxed line-clamp-2">{slides[currentSlide].description}</p>
                    </div>
                  </div>
                </div>
                
                {/* Next button */}
                <button
                  onClick={() => setCurrentSlide(prev => prev === slides.length - 1 ? 0 : prev + 1)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 ml-3 transition-all duration-200 flex-shrink-0"
                >
                  <ChevronRight size={20} className="text-white" />
                </button>
              </div>
              
              {/* Slide indicators */}
              <div className="flex justify-center mt-3 space-x-2">
                {slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      idx === currentSlide 
                        ? 'bg-white' 
                        : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom section - Login Form */}
        <div className="w-full p-6 sm:p-8 lg:p-12 overflow-y-auto">
          {/* Header */}
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
            Sign in to your account
          </h1>
          <p className="text-gray-600 mb-6">
            Enter your credentials to access your dashboard
          </p>

          <div className="space-y-5 max-w-md mx-auto">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail size={18} className="text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" 
                  placeholder="name@company.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                <a href="#" className="text-sm text-blue-600 hover:text-blue-800 font-medium" onClick={openForgotPasswordModal}>
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={18} className="text-gray-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="remember" 
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" 
              />
              <label htmlFor="remember" className="ml-2 text-gray-700 text-sm">
                Remember me for 30 days
              </label>
            </div>

            {/* Error / Activation Messages */}
            {errorMessage === 'activation' ? (
              <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800">
                <p className="font-semibold mb-2">Your account is not activated yet.</p>
                <p className="text-sm">Check your inbox for an activation link.</p>
                <p className="text-sm mt-1">
                  Didn't receive it?&nbsp;
                  <button
                    onClick={handleResendActivation}
                    disabled={resendLoading}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {resendLoading ? 'Sending...' : 'Resend Activation Email'}
                  </button>
                </p>
                {activationResendStatus && (
                  <p className="mt-2 text-sm font-medium text-green-600">{activationResendStatus}</p>
                )}
              </div>
            ) : (
              errorMessage && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errorMessage}
                </div>
              )
            )}

            {/* Sign In Button */}
            <button 
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 text-white font-medium rounded-lg px-5 py-3 text-center transition-colors flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader size={20} className="animate-spin mr-2" />
                  <span>Signing in...</span>
                </>
              ) : (
                "Sign In"
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center space-x-3">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="text-gray-500 text-sm">or continue with</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>

            {/* Google Login */}
            <div className="w-full">
              <GoogleLogin
                onSuccess={(credentialResponse) => {
                  const token = credentialResponse.credential;
                  const userInfo = jwtDecode(token); 
                  //console.log(userInfo);
                  handleGoogleLogin(userInfo);
                }}
                onError={() => console.log("Login Failed")}
                useOneTap
              />
            </div>

            {/* Create Account */}
            <div className="pt-6 text-center">
              <p className="text-gray-600">Don't have an account yet, <Link to="/signup" className="text-blue-600"> Create account</Link>! </p>
            </div>
          </div>
        </div>

      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Reset Password</h2>
              <button
                onClick={closeForgotPasswordModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <p className="text-gray-600 mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="forgotEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail size={18} className="text-gray-400" />
                  </div>
                  <input
                    id="forgotEmail"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    onKeyPress={handleForgotKeyPress}
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="name@company.com"
                  />
                </div>
              </div>
              
              {forgotMessage && (
                <div className={`p-3 rounded-lg flex items-center ${
                  forgotSuccess 
                    ? 'bg-green-100 border border-green-400 text-green-700' 
                    : 'bg-red-100 border border-red-400 text-red-700'
                }`}>
                  {forgotSuccess ? (
                    <CheckCircle size={20} className="mr-2" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                  {forgotMessage}
                </div>
              )}
              
              <div className="flex space-x-3">
                {/*<button
                  onClick={closeForgotPasswordModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                  Cancel
                </button>*/}
                <button
                  onClick={handleForgotPassword}
                  disabled={forgotLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-4 py-2 transition-colors flex items-center justify-center">
                  {forgotLoading ? (
                    <>
                      <Loader size={18} className="animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;